import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash/isEqual';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ExternalSystemId, ModelDTO, ModelGroupDTO } from '../../api/useModelGroupsQuery';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { validation } from './UpsertModelGroup.validation';
import { ModelGroup } from '../ModelGroup/ModelGroup';
import { UpsertModel } from '../UpsertModel/UpsertModel';
import { DeleteModel } from '../DeleteModel/DeleteModel';
import { DeleteModelGroup } from '../DeleteModelGroup/DeleteModelGroup';
import { isTempEntity } from '../../utils/isTempEntity';
import { useUpsertModelGroupMutation } from '../../api/useUpsertModelGroupMutation';
import { useUpsertModelGroup } from './useUpsertModelGroup';
import { validateRiskRanges } from '../../utils/validateRiskRanges';

interface FormValues {
  modelGroupName: string;
}

interface Props {
  modelGroup: Partial<ModelGroupDTO>;
  onClose(): void;
}

export const UpsertModelGroup: React.FC<Props> = ({ modelGroup, onClose }) => {
  const [modelGroupToDelete, setModelGroupToDelete] = useState<Partial<ModelGroupDTO> | null>(null);

  const [modelToEdit, setModelToEdit] = useState<Partial<ModelDTO> | null>(null);

  const [modelToDelete, setModelToDelete] = useState<ModelDTO | null>(null);

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      modelGroupName: modelGroup.groupName || '',
    },
    resolver: yupResolver(validation),
  });

  const {
    isLoading,
    error,
    refetch,
    data: { models, modelsOptions },
    actions: modelsActions,
  } = useUpsertModelGroup({ modelGroup });

  const {
    mutateAsync: upsertModelGroup,
    isLoading: isUpsertingModelGroup,
    error: upsertModelGroupError,
  } = useUpsertModelGroupMutation();

  // Reset the validation error when adding a model
  useEffect(() => {
    if (modelToEdit) {
      setValidationError(null);
    }
  }, [modelToEdit]);

  const isOrion = modelGroup.externalSystem === ExternalSystemId.Orion;
  const isRiskalyze = modelGroup.externalSystem === ExternalSystemId.Riskalyze;

  const onSaveGroup = async ({ modelGroupName }: FormValues) => {
    setValidationError(null);

    if (isLoading) {
      return;
    }

    if (isOrion && models.some(({ orionModelId }) => !orionModelId)) {
      setValidationError('A model must be selected for each Risk Range');

      return;
    }

    if (models.length === 0) {
      setValidationError('Cannot be saved without adding a model');

      return;
    }

    if (!validateRiskRanges(models)) {
      setValidationError('Risk ranges should cover from 1 to 100 without gaps or overlaps');

      return;
    }

    const parsedModels = models.map(model => {
      if (isTempEntity(model.id)) {
        return {
          ...model,
          id: null, // Remove temp ids
        };
      }

      return model;
    });

    try {
      await upsertModelGroup({
        ...modelGroup,
        groupName: modelGroupName,
        modelPortfolios: parsedModels,
      });

      onClose();
    } catch (err) {
      // Ignore
    }
  };

  /**
   * To prevent the user closing the modal without saving all changes
   * ➜ show a confirmation dialog
   */
  const onRequestClose = () => {
    const newModelGroupName = getValues('modelGroupName');

    const newModelGroup = {
      ...modelGroup,
      groupName: newModelGroupName,
      modelPortfolios: models,
    };

    // Do not show confirmation dialog for new model groups
    if (modelGroup.groupId && !isEqual(newModelGroup, modelGroup)) {
      setShowConfirmationDialog(true);
    } else {
      onClose();
    }
  };

  const { groupId, createdBy, externalSystem } = modelGroup;

  return (
    <>
      <Modal
        isOpen={!modelGroupToDelete && !modelToEdit && !modelToDelete && !showConfirmationDialog}
        size="large"
        onRequestClose={onRequestClose}
        contentLabel={`${groupId ? 'Edit' : 'Create'} Model Group`}
      >
        <form onSubmit={handleSubmit(onSaveGroup)}>
          <ModalHeader>{groupId ? 'Edit' : 'Create'} Model Group</ModalHeader>

          <ModalCloseButton onClick={onRequestClose} />

          <ModalBody>
            {!isLoading && error && (
              <Alert
                message={error.message}
                variant="error"
                mb="16"
                actions={[{ text: 'Try again', action: refetch }]}
              />
            )}

            {validationError && <Alert message={validationError} variant="error" mb="16" />}

            {upsertModelGroupError && (
              <Alert message={upsertModelGroupError.message} variant="error" mb="16" />
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box width={450}>
                <Input
                  {...register('modelGroupName')}
                  label="Model Group Name"
                  type="text"
                  formVariant={errors.modelGroupName ? 'error' : undefined}
                  helperText={errors.modelGroupName?.message}
                  autoFocus
                />
              </Box>

              {!isRiskalyze && (
                <Button
                  variant="subtleActive"
                  leftIcon="far fa-plus-circle"
                  onClick={() => {
                    if (isOrion) {
                      modelsActions.addNewModel();
                    } else {
                      setModelToEdit({});
                    }
                  }}
                  mt="24"
                >
                  {isOrion ? 'Add Risk Range' : 'Add Model'}
                </Button>
              )}
            </Box>

            {createdBy && (
              <Box mt="16">
                <Text fontWeight={500}>Created by {createdBy.name}</Text>
                <Text fontSize="small" color="neutral.500">
                  Email: {createdBy.email}
                </Text>
              </Box>
            )}
          </ModalBody>

          <ModelGroup
            isLoading={isLoading}
            models={models}
            modelsActions={modelsActions}
            modelGroup={modelGroup}
            modelsOptions={modelsOptions}
            setModelToEdit={setModelToEdit}
            setModelToDelete={setModelToDelete}
            onModelSelection={() => setValidationError(null)}
          />

          <ModalFooter>
            {groupId && (
              <Box flex={1}>
                <Button
                  variant="error"
                  onClick={() => setModelGroupToDelete(modelGroup)}
                  disabled={isUpsertingModelGroup}
                >
                  Delete
                </Button>
              </Box>
            )}

            <Button
              variant="subtleInactive"
              onClick={onRequestClose}
              disabled={isUpsertingModelGroup}
            >
              Cancel
            </Button>

            <Button variant="primary" ml="12" type="submit" isLoading={isUpsertingModelGroup}>
              Save Model Group
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {modelToEdit && (
        <UpsertModel
          isReadOnly={externalSystem === ExternalSystemId.Riskalyze}
          model={modelToEdit}
          models={models}
          modelsActions={modelsActions}
          externalSystem={externalSystem}
          onClose={() => setModelToEdit(null)}
        />
      )}

      {modelToDelete && (
        <DeleteModel
          model={modelToDelete}
          models={models}
          modelsActions={modelsActions}
          onClose={() => setModelToDelete(null)}
        />
      )}

      {modelGroupToDelete && (
        <DeleteModelGroup
          modelGroup={modelGroup}
          onClose={() => {
            setModelGroupToDelete(null);
          }}
          onUpsertModelGroupClose={onClose}
        />
      )}

      {showConfirmationDialog && (
        <Modal
          isOpen
          contentLabel="Delete Model Group confirmation dialog"
          onRequestClose={() => setShowConfirmationDialog(false)}
        >
          <ModalHeader variant="warning">Warning</ModalHeader>
          <ModalCloseButton onClick={() => setShowConfirmationDialog(false)} />

          <ModalBody>Are you sure you want to leave without saving this Model Group?</ModalBody>

          <ModalFooter>
            <Button
              variant="subtleInactive"
              onClick={() => setShowConfirmationDialog(false)}
              mr="8"
            >
              Cancel
            </Button>

            <Button variant="primary" onClick={onClose}>
              Ok
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};
